import { jwtDecode } from 'jwt-decode';
import { safeGetItem, safeNavigate, safeRemoveItem, safeSetItem } from '../utils/localStorage';

/**
 * « Se connecter en tant que » (espace /superadmin) : le jeton du superadmin est mis de côté le temps de
 * l'impersonation, puis restauré au retour (ou à l'expiration du jeton d'impersonation, 30 min).
 */
const SUPERADMIN_TOKEN_KEY = 'impersonation.superadmin_token';
const RETURN_PATH_KEY = 'impersonation.return_path';
const TARGET_KEY = 'impersonation.target';

export interface ImpersonationTarget {
  id: number;
  email: string | null;
  nom: string | null;
  prenom: string | null;
  expiresAt: string;
}

export function isImpersonating(): boolean {
  const token = safeGetItem('token');
  if (!token || !safeGetItem(SUPERADMIN_TOKEN_KEY)) return false;
  try {
    return Boolean(jwtDecode<{ impersonated_by?: number }>(token).impersonated_by);
  } catch {
    return false;
  }
}

export function getImpersonationTarget(): ImpersonationTarget | null {
  if (!isImpersonating()) return null;
  try {
    return JSON.parse(safeGetItem(TARGET_KEY) ?? 'null');
  } catch {
    return null;
  }
}

/** Bascule sur le compte de l'utilisateur : rechargement complet pour repartir d'un cache vide. */
export function startImpersonation(token: string, target: ImpersonationTarget, returnPath: string): void {
  const currentToken = safeGetItem('token');
  if (!currentToken) return;
  // Pas d'impersonation imbriquée : on garde toujours le jeton du superadmin d'origine.
  if (!safeGetItem(SUPERADMIN_TOKEN_KEY)) safeSetItem(SUPERADMIN_TOKEN_KEY, currentToken);
  safeSetItem(RETURN_PATH_KEY, returnPath);
  safeSetItem(TARGET_KEY, JSON.stringify(target));
  safeSetItem('token', token);
  safeNavigate('/admin/dashboard');
}

/** Revient au compte superadmin (fin volontaire ou jeton d'impersonation expiré). */
export function stopImpersonation(): boolean {
  const superAdminToken = safeGetItem(SUPERADMIN_TOKEN_KEY);
  if (!superAdminToken) return false;
  const returnPath = safeGetItem(RETURN_PATH_KEY) || '/superadmin/clients';
  safeSetItem('token', superAdminToken);
  safeRemoveItem(SUPERADMIN_TOKEN_KEY);
  safeRemoveItem(RETURN_PATH_KEY);
  safeRemoveItem(TARGET_KEY);
  safeNavigate(returnPath);
  return true;
}
